import { useState, useEffect } from 'react';
import { SupportedLocale, SUPPORTED_LOCALES, normalizeLocale, extractTextFromResponse } from '../utils';

type FetchObjectParams = {
  key: string;
  responseType?: 'text' | 'arraybuffer' | 'blob';
};

type GetObjectFn = (params: FetchObjectParams) => Promise<unknown>;

type GetObjectUrlFn = (params: { key: string }) => Promise<{ url: string }>;

export interface UseLowcodeCosTemplateParams {
  templateConfig: IRecord;
  locale: string;
  isCOSInited: boolean;
  getObject: GetObjectFn;
  getObjectUrl: GetObjectUrlFn;
}

export interface UseLowcodeCosTemplateResult {
  loading: boolean;
  error?: unknown;
  htmlContent: string;
  jsonData: unknown;
}

export default function useLowcodeCosTemplate(props: UseLowcodeCosTemplateParams): UseLowcodeCosTemplateResult {
  const { templateConfig, locale, isCOSInited, getObject, getObjectUrl } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [jsonDataMap, setJsonDataMap] = useState<Partial<Record<SupportedLocale, unknown>>>({});

  const fetchTemplateAndData = async (config: IRecord) => {
    setLoading(true);
    setError(null);

    const errors: Error[] = [];
    let fetchedHtmlContent = '';
    const newJsonDataMap: Partial<Record<SupportedLocale, unknown>> = {};

    // 获取 HTML 模板路径
    const templatePath = config?.templatePath as string | undefined;

    // 构建 JSON 数据路径映射
    const localeDataPaths: Partial<Record<SupportedLocale, string>> = {};
    SUPPORTED_LOCALES.forEach((loc) => {
      const key = `dataPath_${loc}` as const;
      const value = config?.[key];
      if (typeof value === 'string' && value) {
        localeDataPaths[loc] = value;
      }
    });

    // 并行执行所有请求
    const promises: Promise<void>[] = [];

    // 1. 获取 HTML 模板
    if (templatePath) {
      promises.push(
        (async () => {
          try {
            console.log('[LowCodeTemplateWithDataJSON] Fetching HTML template:', templatePath);
            const body = await getObject({ key: templatePath, responseType: 'text' });
            fetchedHtmlContent = await extractTextFromResponse(body);
          } catch (err) {
            console.error('[LowCodeTemplateWithDataJSON] Failed to fetch HTML template:', err);
            errors.push(err instanceof Error ? err : new Error(String(err)));
          }
        })(),
      );
    }

    // 2. 并行获取所有语言的 JSON 数据
    const localePathEntries = Object.entries(localeDataPaths) as Array<[SupportedLocale, string]>;
    localePathEntries.forEach(([loc, path]) => {
      promises.push(
        (async () => {
          try {
            console.log('[LowCodeTemplateWithDataJSON] Fetching JSON data for locale:', loc, path);
            const { url } = await getObjectUrl({ key: path });
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch JSON for ${loc}: ${response.statusText}`);
            }
            newJsonDataMap[loc] = await response.json();
          } catch (err) {
            console.error(`[LowCodeTemplateWithDataJSON] Failed to fetch JSON data for ${loc}:`, err);
            errors.push(err instanceof Error ? err : new Error(String(err)));
          }
        })(),
      );
    });

    await Promise.all(promises);

    // 降级处理：只有当所有请求都失败时才设置错误
    // 如果有部分成功，则继续使用成功的数据
    if (errors.length > 0 && errors.length === promises.length) {
      setError(errors[0]);
    }

    setHtmlContent(fetchedHtmlContent);
    setJsonDataMap(newJsonDataMap);
    setLoading(false);
  };

  useEffect(() => {
    if (!isCOSInited || !templateConfig) {
      if (!templateConfig) {
        setLoading(false);
      }
      return;
    }

    void fetchTemplateAndData(templateConfig);
  }, [isCOSInited, templateConfig]);

  // 根据当前 locale 获取对应的 JSON 数据
  const normalizedLocale = normalizeLocale(locale);
  const jsonData = normalizedLocale && jsonDataMap[normalizedLocale] ? jsonDataMap[normalizedLocale] : undefined;

  return {
    loading,
    error,
    htmlContent,
    jsonData,
  };
}
